/**
 * Boundary module for `PlannedDate` values.
 *
 * All functions are pure and treat a `PlannedDate` as floating: the `date`/`time`
 * components are formatted literally, with NO timezone conversion. This is what
 * eliminates the round-trip edit-drift and the date-only off-by-one that the old
 * `dateUtils` UTC path suffered from.
 *
 * Fixed-zone (`timeZoneId`) values are labelled with their zone; the viewer-local
 * "· your time" conversion is layered on top in the display components.
 */

import { format } from 'date-fns';

import type { DateFormat, TimeFormat } from '@/core/stores/ui.store';
import type { PlainDate, PlannedDate } from '@/shared/types/datetime';

export interface FormatPlannedDateOptions {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  /** When true, fixed-zone values append ` (Zone)`; floating values never do. */
  showZoneLabel?: boolean;
}

/** Parse `yyyy-MM-dd` into `[year, month, day]` (month is 1-based). */
function parseDateParts(date: PlainDate): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number);
  return [y, m, d];
}

/**
 * A local `Date` anchored at noon on the calendar day.
 *
 * Noon avoids any DST/offset rollover pushing the date to the adjacent day, so
 * literal formatting and calendar libraries (Syncfusion) always render the right
 * day regardless of the browser timezone.
 */
export function plannedToAnchorDate(value: PlannedDate): Date {
  const [y, m, d] = parseDateParts(value.date);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Build a local `Date` for an `HH:mm` time on an arbitrary day (for formatting only). */
function timeToAnchorDate(time: string): Date {
  const [h, min] = time.split(':').map(Number);
  return new Date(2000, 0, 1, h, min, 0, 0);
}

/** Human-friendly zone label: `America/Mexico_City` → `Mexico City`. */
export function shortTimeZoneLabel(timeZoneId: string): string {
  const last = timeZoneId.split('/').pop() ?? timeZoneId;
  return last.replace(/_/g, ' ');
}

/** Format the time component of a `PlannedDate` using the user's time format. */
export function formatPlannedTime(value: PlannedDate, timeFormat: TimeFormat): string {
  if (value.time == null) return '';
  return format(timeToAnchorDate(value.time), timeFormat);
}

/**
 * Format a `PlannedDate` for display, literally (no timezone conversion).
 *
 * - date-only → the date in the user's date format.
 * - floating time → `date time`.
 * - fixed-zone → `date time (Zone)` when `showZoneLabel` is set.
 */
export function formatPlannedDate(
  value: PlannedDate | null | undefined,
  options: FormatPlannedDateOptions,
): string {
  if (!value) return '';

  const datePart = format(plannedToAnchorDate(value), options.dateFormat);
  if (value.time == null) return datePart;

  const timePart = formatPlannedTime(value, options.timeFormat);
  const base = `${datePart} ${timePart}`;

  if (options.showZoneLabel && value.timeZoneId != null) {
    return `${base} (${shortTimeZoneLabel(value.timeZoneId)})`;
  }
  return base;
}

/** Sortable, lexicographically-ordered key. Null time sorts as start of day. */
export function plannedToSortKey(value: PlannedDate): string {
  return `${value.date}T${value.time ?? '00:00'}`;
}

/**
 * Calendar ordering: by `date`, then `time` (a missing time sorts as start of day).
 * Zone is intentionally ignored — ordering is by the floating wall-clock.
 */
export function comparePlannedDate(a: PlannedDate, b: PlannedDate): number {
  const keyA = plannedToSortKey(a);
  const keyB = plannedToSortKey(b);
  if (keyA < keyB) return -1;
  if (keyA > keyB) return 1;
  return 0;
}

/** Structural equality across `date`, `time` and `timeZoneId` (null-safe). */
export function arePlannedDatesEqual(
  a: PlannedDate | null | undefined,
  b: PlannedDate | null | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return (
    a.date === b.date &&
    (a.time ?? null) === (b.time ?? null) &&
    (a.timeZoneId ?? null) === (b.timeZoneId ?? null)
  );
}

/** Shift the calendar day by `days`, preserving `time` and `timeZoneId`. */
export function addDays(value: PlannedDate, days: number): PlannedDate {
  const anchor = plannedToAnchorDate(value);
  anchor.setDate(anchor.getDate() + days);
  return { ...value, date: format(anchor, 'yyyy-MM-dd') };
}

/** Whole-day difference `to - from` by calendar date (time/zone ignored). */
export function daysBetween(from: PlannedDate, to: PlannedDate): number {
  const a = plannedToAnchorDate(from).getTime();
  const b = plannedToAnchorDate(to).getTime();
  return Math.round((b - a) / 86_400_000);
}

/**
 * Minutes between two `PlannedDate`s, floating (no timezone conversion).
 *
 * When both carry a `time`, computed to the minute from the actual wall-clock
 * date+time. When either is date-only, the dateless side anchors to its natural
 * day boundary (`00:00` for `from`, `23:59` for `to`) — there is no working-hours
 * concept in this codebase, so a missing time falls back to the full calendar
 * span rather than an assumed work day, keeping the result a looser (not
 * under-counted) upper bound.
 */
export function minutesBetween(from: PlannedDate, to: PlannedDate): number {
  const [fh, fm] = (from.time ?? '00:00').split(':').map(Number);
  const [th, tm] = (to.time ?? '23:59').split(':').map(Number);

  const fromDate = plannedToAnchorDate(from);
  fromDate.setHours(fh, fm, 0, 0);
  const toDate = plannedToAnchorDate(to);
  toDate.setHours(th, tm, 0, 0);

  return Math.round((toDate.getTime() - fromDate.getTime()) / 60_000);
}
